import React, { useState } from "react";
import { History, Repeat2, Thermometer } from "lucide-react";
import { toast } from "sonner";
import { athleteGetGearHistory } from "../../lib/api";

const FEELING_LABEL = { very_weak: "Very weak", weak: "Weak", normal: "Normal", strong: "Strong", very_strong: "Very strong" };

/** Optional conditions + kit record for a run log, with a history panel. */
export default function GearLogSection({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const inputCls = "w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none";

  const toggleHistory = async () => {
    const next = !open;
    setOpen(next);
    if (next && hist === null) {
      try { setHist(await athleteGetGearHistory()); } catch { setHist([]); }
    }
  };

  return (
    <div data-testid="gear-section" className="pt-5 border-t border-dashed border-[var(--tir-border)]">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
          <Thermometer size={10} className="inline mr-1 -mt-0.5" />
          Conditions &amp; gear (optional)
        </label>
        <button
          type="button"
          onClick={toggleHistory}
          data-testid="gear-history-btn"
          className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
        >
          <History size={11} /> History
        </button>
      </div>

      {open && (
        <div className="mb-2 border border-[var(--tir-border)] bg-[var(--tir-ivory)]/60 rounded-sm p-2 space-y-2 max-h-56 overflow-y-auto" data-testid="gear-history-panel">
          {hist === null ? (
            <div className="text-[11px] italic text-[var(--tir-muted)]">Loading…</div>
          ) : hist.length === 0 ? (
            <div className="text-[11px] italic text-[var(--tir-muted)]">No gear logged yet — fill this in on a few runs and it becomes your what-to-wear guide.</div>
          ) : (
            hist.map((h, i) => {
              const g = h.gear || {};
              const when = h.updated_at || h.logged_at;
              return (
                <div key={i} className="flex items-start gap-2" data-testid={`gear-history-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-mono-tir text-[var(--tir-muted)]">
                      {when ? new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                      {h.distance_actual_km ? ` · ${h.distance_actual_km} km` : ""}
                      {h.duration_actual_min ? ` · ${Math.round(h.duration_actual_min)} min` : ""}
                      {h.feeling ? ` · felt ${FEELING_LABEL[h.feeling] || h.feeling}` : ""}
                    </div>
                    <div className="text-[11px] text-[var(--tir-ink)] leading-snug">
                      {g.temperature && <span className="font-semibold">{g.temperature}</span>}
                      {g.temperature && (g.shoes || g.layers) ? " — " : ""}
                      {[g.shoes, g.layers].filter(Boolean).join(" · ")}
                    </div>
                    {g.note && <div className="text-[11px] italic text-[var(--tir-muted)] leading-snug">“{g.note}”</div>}
                  </div>
                  <button
                    type="button"
                    title="Copy into this log"
                    data-testid={`gear-history-use-${i}`}
                    onClick={() => { onChange({ temperature: g.temperature || "", shoes: g.shoes || "", layers: g.layers || "", note: "" }); toast.success("Copied — tweak as needed"); }}
                    className="shrink-0 text-[var(--tir-navy)] hover:text-[var(--tir-burgundy)] p-0.5"
                  >
                    <Repeat2 size={13} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <label className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">Temperature</label>
          <input value={value.temperature || ""} onChange={set("temperature")} placeholder="e.g. -5°C, windy" data-testid="gear-temperature-input" className={inputCls} />
        </div>
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <label className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">Shoes</label>
          <input value={value.shoes || ""} onChange={set("shoes")} placeholder="e.g. Speedgoat 6" data-testid="gear-shoes-input" className={inputCls} />
        </div>
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <label className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">Gear / Layers</label>
          <input value={value.layers || ""} onChange={set("layers")} placeholder="e.g. tights, long sleeve + vest, buff, gloves" data-testid="gear-layers-input" className={inputCls} />
        </div>
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <label className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">How it worked</label>
          <input value={value.note || ""} onChange={set("note")} placeholder="e.g. perfect — hands cold first 10 min" data-testid="gear-note-input" className={inputCls} />
        </div>
      </div>
    </div>
  );
}
